// Google Analytics 4 adapter: pages, conversions, Web Vitals, consent mode.
// Env-gated: without a measurement id, every function is a silent no-op.

const gaId = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
let ready = false;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function gtag(...args: unknown[]): void {
  window.dataLayer?.push(args);
}

export function gaInit(consented: boolean): void {
  if (!gaId || ready) return;
  window.dataLayer = window.dataLayer ?? [];
  window.gtag = gtag;

  // Consent mode default: denied until the consent center says otherwise.
  gtag("consent", "default", {
    analytics_storage: consented ? "granted" : "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  document.head.appendChild(s);

  gtag("js", new Date());
  gtag("config", gaId, { send_page_view: false, debug_mode: import.meta.env.DEV });
  ready = true;
}

export function gaConsent(granted: boolean): void {
  if (!ready) return;
  gtag("consent", "update", { analytics_storage: granted ? "granted" : "denied" });
}

export function gaPage(path: string, title: string): void {
  if (!ready) return;
  gtag("event", "page_view", { page_path: path, page_title: title });
}

export function gaEvent(name: string, props: Record<string, unknown>): void {
  if (!ready) return;
  gtag("event", name, props);
}

export function gaAvailable(): boolean {
  return Boolean(gaId);
}

/** UTM persistence: capture campaign attribution once per session. */
export function captureUtm(): Record<string, string> {
  const params = new URLSearchParams(location.search);
  const utm: Record<string, string> = {};
  for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]) {
    const v = params.get(k);
    if (v) utm[k] = v;
  }
  if (Object.keys(utm).length > 0) {
    try {
      sessionStorage.setItem("bd.utm", JSON.stringify(utm));
    } catch {
      /* session storage unavailable */
    }
  }
  try {
    return JSON.parse(sessionStorage.getItem("bd.utm") ?? "{}") as Record<string, string>;
  } catch {
    return utm;
  }
}
